/**
 * Toggle — switch on/off accessible.
 *
 * @remarks
 * Implémentation custom (pas de Radix Switch dans nos deps actuelles).
 * Mêmes ARIA props que `<button role="switch">` standard.
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

export interface ToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  size?: 'sm' | 'md';
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(function Toggle(
  { checked, onCheckedChange, size = 'md', className, disabled, ...rest },
  ref,
) {
  const sizes =
    size === 'sm'
      ? { track: 'h-4 w-7', thumb: 'size-3', tx: 'translate-x-3' }
      : { track: 'h-5 w-9', thumb: 'size-4', tx: 'translate-x-4' };
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        onCheckedChange(!checked);
      }}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full border border-transparent transition-colors',
        'focus-visible:outline-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary-500' : 'bg-border-strong',
        sizes.track,
        className,
      )}
      {...rest}
    >
      <span
        className={cn(
          'inline-block transform rounded-full bg-white shadow-sm transition-transform',
          sizes.thumb,
          checked ? sizes.tx : 'translate-x-0.5',
        )}
      />
    </button>
  );
});
